import { CSSProperties, ReactNode, useEffect, useState } from "react";
import "./InvitationLoader.css";

export type InvitationLoaderSize = "sm" | "md";

export interface InvitationLoaderStage {
  label: string;
  icon?: ReactNode;
}

export interface InvitationLoaderProps {
  size?: InvitationLoaderSize;
  stages?: InvitationLoaderStage[];
  stageDuration?: number;
  showLabel?: boolean;
  color?: string;
  className?: string;
  style?: CSSProperties;
}

const EnvelopeIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect x="3" y="6" width="18" height="14" rx="2" />
    <path d="M3 7 L12 14 L21 7" />
  </svg>
);

const KeyIcon = () => (
  <svg className="il-icon-key" viewBox="0 0 24 24">
    <circle cx="8" cy="14" r="3.5" />
    <path d="M11 13 L21 3" />
    <path d="M17 7 L20 10" />
  </svg>
);

const ClipboardCheckIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M9 11 l3 3 L22 4" />
    <path d="M21 12 v7 a2 2 0 0 1 -2 2 H5 a2 2 0 0 1 -2 -2 V5 a2 2 0 0 1 2 -2 h11" />
  </svg>
);

const DEFAULT_STAGES: InvitationLoaderStage[] = [
  { label: "Loading invitation", icon: <EnvelopeIcon /> },
  { label: "Verifying access", icon: <KeyIcon /> },
  { label: "Almost ready", icon: <ClipboardCheckIcon /> },
];

export function InvitationLoader({
  size = "md",
  stages = DEFAULT_STAGES,
  stageDuration = 1.8,
  showLabel = true,
  color,
  className,
  style,
}: InvitationLoaderProps) {
  const [active, setActive] = useState(0);
  const [prev, setPrev] = useState(-1);

  useEffect(() => {
    if (stages.length <= 1) return;
    const id = window.setInterval(() => {
      setActive((curr) => {
        setPrev(curr);
        return (curr + 1) % stages.length;
      });
    }, Math.max(0.5, stageDuration) * 1000);
    return () => window.clearInterval(id);
  }, [stages.length, stageDuration]);

  const cssVars: CSSProperties = {
    ...(color ? ({ ["--il-color" as never]: color } as CSSProperties) : {}),
    ...style,
  };

  const stageClass = (i: number) => {
    if (i === active) return "il-stage-item is-active";
    if (i === prev) return "il-stage-item is-prev";
    return "il-stage-item";
  };

  const rootClass = ["il-root", `il-${size}`, className].filter(Boolean).join(" ");

  return (
    <div className={rootClass} style={cssVars} role="status" aria-live="polite">
      <div className="il-stage">
        <svg className="il-svg" viewBox="0 0 240 240">
          <rect className="il-env-back" x="20" y="80" width="200" height="135" rx="8" />
          <path className="il-pocket" d="M 22 213 L 120 148 L 218 213" />
          <g className="il-flap-group">
            <path className="il-flap" d="M 22 80 L 218 80 L 120 148 Z" />
          </g>
          <g className="il-letter-group">
            <rect className="il-letter-bg" x="42" y="78" width="156" height="118" rx="5" />
            <rect className="il-letter-logo" x="58" y="92" width="22" height="22" rx="5" />
            <rect className="il-letter-line il-l1" x="88" y="98" width="92" height="4" rx="2" />
            <rect className="il-letter-line il-l2" x="58" y="128" width="124" height="4" rx="2" />
            <rect className="il-letter-line il-l3" x="58" y="140" width="100" height="4" rx="2" />
            <rect className="il-letter-cta" x="58" y="162" width="56" height="6" rx="3" />
          </g>
        </svg>
      </div>

      {showLabel && stages.length > 0 && (
        <div className="il-stages">
          <div className="il-stage-icon-wrap">
            {stages.map((stage, i) => (
              <div key={i} className={stageClass(i)}>{stage.icon}</div>
            ))}
          </div>
          <div className="il-stage-text-wrap">
            {stages.map((stage, i) => (
              <div key={i} className={stageClass(i)}>
                <span className="il-stage-text">
                  {stage.label}
                  <span className="il-dots" />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default InvitationLoader;
